import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'

/**
 * The per-room figures a bedbank enquiry is built from. All three are on svc-bedbank's own,
 * un-marked basis: svc-bedbank is unaware of channel markup, and checkout and svc-order re-mark
 * whatever price it validates, so sending a marked price marks the same rate twice.
 */
export interface BedbankEnquiryTotals {
  price: number
  memberPrice: number
  propertyFees: number
}

/**
 * Read a room's un-marked prices straight off its enquiry session.
 *
 * The session stores svc-bedbank's base prices; the checkout item view marks them for display, so
 * the view's `price` is the wrong figure to re-enquire with. `memberPrice` keeps the view's gating
 * (LuxPlus+ LPP pricing disabled shows no member price) and only swaps in the un-marked amount.
 */
export function getBedbankSessionEnquiryTotals(
  session: App.BedbankEnquiry | null | undefined,
  occupancy: App.Occupants | undefined,
  viewTotals: App.Checkout.ItemViewTotals,
): BedbankEnquiryTotals | undefined {
  const occupancyPricing = session?.occupancyPricing
  if (!occupancyPricing?.length) return
  const occupancyKey = occupancy ? generateOccupancyStringByRoom(occupancy) : undefined
  // Match this room's occupancy. Only fall back to the sole entry when there is nothing to match on
  // (no occupancy) or the session holds a single room — never price a multi-room session's room off
  // the first entry, which would silently repeat one room's price across differently-occupied rooms.
  // A genuine multi-room key miss returns `undefined` so the caller falls back rather than mis-prices.
  const pricing = occupancyKey === undefined || occupancyPricing.length === 1
    ? occupancyPricing[0]
    : occupancyPricing.find(pricing => pricing.occupancy === occupancyKey)
  if (!pricing) return
  return {
    price: pricing.inclusive,
    // The view gates whether a member price applies (LuxPlus+ LPP display). When it does, send the
    // un-marked member base if the session carries one, else the un-marked public base: a member with
    // no member rate on this room pays the public price, so it must not drop to 0 (under-reporting the
    // member basis) nor reuse the marked view figure (which checkout would re-mark).
    memberPrice: viewTotals.memberPrice ? (pricing.memberInclusive ?? pricing.inclusive) : 0,
    propertyFees: pricing.propertyFees ?? viewTotals.otherFees?.propertyFees ?? 0,
  }
}

/**
 * Last-resort figures for an item with neither a session nor a loyalty quote to read a base off.
 *
 * `viewTotals.price` is the *marked* display price, so on this path a channel-marked rate is sent to
 * svc-bedbank and re-marked downstream — a known limitation, not a correct base. It is tolerable only
 * because the un-marked base does not exist anywhere on a plain bedbank item or its view (the rate's
 * `occupancyPricing` and `ItemViewTotals` both hold only the marked price); the base lives on the
 * session and on loyalty `PropertyRateTotals`, both preferred above. Prefer those; reach here only
 * when the session is genuinely absent.
 */
export function getItemViewEnquiryTotals(viewTotals: App.Checkout.ItemViewTotals): BedbankEnquiryTotals {
  return {
    price: viewTotals.price,
    memberPrice: viewTotals.memberPrice,
    propertyFees: viewTotals.otherFees?.propertyFees ?? 0,
  }
}
